import React, { useEffect, useState, useCallback } from "react";
import { Target, Loader2, Check, Pencil } from "lucide-react";
import api from "../../api";
import { useLang } from "../../context/LanguageContext";

export const RedemptionGoalCard = ({ compact = false }) => {
  const { t } = useLang();
  const [goal, setGoal] = useState(null); // {target, redeemedThisMonth, percent}
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/my/redemption-goal"); setGoal(data); setValue(String(data.target || "")); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const target = Math.max(0, parseInt(value || "0", 10) || 0);
      await api.put("/my/business", { redemptionTarget: target });
      await load();
      setEditing(false);
    } finally { setSaving(false); }
  };

  if (!goal) return null;
  const { target, redeemedThisMonth, percent } = goal;
  const done = target > 0 && redeemedThisMonth >= target;

  return (
    <div className={`bg-card border border-border rounded-2xl p-5 ${compact ? "" : ""}`} data-testid="redemption-goal-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="font-bold text-foreground flex items-center gap-2"><Target size={18} className="text-[#16a34a]" /> {t("rgc.monthlyRedemptionGoal")}</h3>
        {!editing && (
          <button onClick={() => setEditing(true)} data-testid="edit-goal-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#16a34a] hover:underline">
            <Pencil size={13} /> {target > 0 ? "Edit target" : "Set target"}
          </button>
        )}
      </div>

      {editing ? (
        <div className="flex flex-wrap items-center gap-2">
          <input type="number" min="0" value={value} onChange={(e) => setValue(e.target.value)} placeholder="e.g. 50" data-testid="goal-target-input" className="w-32 border border-border rounded-xl px-3 py-2.5 bg-transparent outline-none text-sm text-foreground focus:border-[#16a34a]" />
          <span className="text-sm text-muted-foreground">{t("rgc.redemptionsMonth")}</span>
          <button onClick={save} disabled={saving} data-testid="save-goal-btn" className="inline-flex items-center gap-1.5 bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors">{saving ? <Loader2 className="animate-spin" size={15} /> : <Check size={15} />} Save</button>
          <button onClick={() => { setEditing(false); setValue(String(target || "")); }} className="text-sm text-muted-foreground hover:text-foreground px-2">Cancel</button>
        </div>
      ) : target === 0 ? (
        <p className="text-sm text-muted-foreground">{t("rgc.setAMonthlyTarget")}</p>
      ) : (
        <>
          <div className="flex items-end justify-between mb-2">
            <p className="text-2xl font-extrabold text-foreground" data-testid="goal-progress-text">{redeemedThisMonth}<span className="text-base font-semibold text-muted-foreground"> / {target}</span></p>
            <span className={`text-sm font-bold ${done ? "text-[#16a34a]" : "text-muted-foreground"}`}>{percent}%</span>
          </div>
          <div className="h-3 rounded-full bg-secondary overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-500 ${done ? "bg-[#16a34a]" : "bg-[#16a34a]/70"}`} style={{ width: `${percent}%` }} data-testid="goal-progress-bar" />
          </div>
          <p className="text-xs mt-2 font-semibold flex items-center gap-1" data-testid="goal-status">
            {done ? <span className="text-[#16a34a] inline-flex items-center gap-1"><Check size={13} /> {t("rgc.goalReachedNicelyDone")}</span>
                  : <span className="text-muted-foreground">{target - redeemedThisMonth} more redemption{target - redeemedThisMonth === 1 ? "" : "s"} to hit this month's goal</span>}
          </p>
        </>
      )}
    </div>
  );
};
